import { ageOn } from "../players/age.js";
import { parseAmountText } from "./amount.js";
import { parseDateText, parseDurationText, parseEmailText, parseFlagText, parsePhoneText, } from "./fields.js";
import {} from "./template.js";
const SEVERITY = {
    'missing-username': 'error',
    'bad-amount': 'error',
    'bad-time': 'error',
    'other-currency': 'error',
    'duplicate-source-id': 'error',
    'username-clash': 'error',
    'time-group-unmapped': 'error',
    'negative-cash': 'warning',
    'negative-time': 'warning',
    'over-wallet-cap': 'warning',
    'duplicate-email': 'warning',
    'duplicate-phone': 'warning',
    'email-invalid': 'warning',
    'email-blocked': 'warning',
    'phone-invalid': 'warning',
    'date-invalid': 'warning',
    'flag-unclear': 'warning',
    'bad-bonus': 'warning',
    minor: 'warning',
    'house-account': 'warning',
    'deleted-in-source': 'warning',
    'large-balance': 'info',
    'bonus-dropped': 'info',
    'existing-account': 'info',
    'counter-only': 'info',
    'already-imported': 'info',
};
export function migrationIssueSeverity(code) {
    return SEVERITY[code];
}
const LIMITS = { username: 64, name: 80, note: 2000, banReason: 500, sourceId: 128 };
/** Trim, drop control characters, and cut to `max` characters; empty is null. */
function cleanText(value, max) {
    if (value === undefined)
        return null;
    // eslint-disable-next-line no-control-regex
    const cleaned = value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').trim();
    if (cleaned === '')
        return null;
    return cleaned.length > max ? cleaned.slice(0, max) : cleaned;
}
/** Read one source row through the mapping. Never throws: what cannot be read becomes an issue. */
export function readMigrationRow(cells, rowNumber, mapping, options) {
    const cell = (field) => {
        const index = mapping[field];
        return index === undefined ? undefined : cells[index];
    };
    const issues = [];
    const sourceId = cleanText(cell('sourceId'), LIMITS.sourceId);
    const username = cleanText(cell('username'), LIMITS.username);
    if (username === null)
        issues.push({ code: 'missing-username', field: 'username' });
    const currencyCell = cleanText(cell('currency'), 8);
    if (currencyCell !== null && currencyCell.toUpperCase() !== options.currency.toUpperCase())
        issues.push({ code: 'other-currency', field: 'currency' });
    const money = (field) => {
        const parsed = parseAmountText(cell(field), { currency: options.currency, unit: options.moneyUnit });
        if (parsed.kind === 'ok')
            return parsed.minor;
        if (parsed.kind === 'error') {
            if (field === 'bonusBalance')
                issues.push({ code: 'bad-bonus', field });
            else
                issues.push({ code: parsed.reason === 'other-currency' ? 'other-currency' : 'bad-amount', field });
        }
        return 0;
    };
    const cashMinor = money('cashBalance');
    const bonusMinor = money('bonusBalance');
    let timeSeconds = 0;
    const time = parseDurationText(cell('timeBalance'), options.timeUnit);
    if (time.kind === 'ok')
        timeSeconds = time.seconds;
    else if (time.kind === 'error')
        issues.push({ code: 'bad-time', field: 'timeBalance' });
    const date = (field) => {
        const parsed = parseDateText(cell(field), options.dateOrder, { allowSerial: options.allowSerialDates === true });
        if (parsed.kind === 'ok')
            return parsed.date;
        if (parsed.kind === 'error')
            issues.push({ code: 'date-invalid', field });
        return null;
    };
    let email = null;
    const parsedEmail = parseEmailText(cell('email'));
    if (parsedEmail.kind === 'ok')
        email = parsedEmail.email;
    else if (parsedEmail.kind === 'error')
        issues.push({ code: 'email-invalid', field: 'email' });
    let phone = null;
    const parsedPhone = parsePhoneText(cell('phone'), options.region);
    if (parsedPhone.kind === 'ok')
        phone = parsedPhone.e164;
    else if (parsedPhone.kind === 'error')
        issues.push({ code: 'phone-invalid', field: 'phone' });
    const flag = (field) => {
        const value = parseFlagText(cell(field));
        if (value === null) {
            issues.push({ code: 'flag-unclear', field });
            return false;
        }
        return value;
    };
    const groupsCell = cleanText(cell('timePcGroups'), 500);
    const timePcGroups = groupsCell === null ? [] : [...new Set(groupsCell.split(/[;|,]/).map((group) => group.trim()).filter((group) => group !== ''))];
    return {
        rowNumber,
        sourceKey: sourceId ?? `u:${(username ?? '').toLowerCase()}`,
        sourceId,
        username,
        firstName: cleanText(cell('firstName'), LIMITS.name),
        lastName: cleanText(cell('lastName'), LIMITS.name),
        email,
        phone,
        dateOfBirth: date('dateOfBirth'),
        joinedOn: date('joinedOn'),
        lastVisitOn: date('lastVisitOn'),
        cashMinor,
        bonusMinor,
        timeSeconds,
        timePcGroups,
        banned: flag('banned'),
        banReason: cleanText(cell('banReason'), LIMITS.banReason),
        note: cleanText(cell('note'), LIMITS.note),
        deleted: flag('deleted'),
        issues,
    };
}
/** The station classes a record's time may be used on: the union of its groups' classes, or the fallback. */
export function stationClassesForTime(record, classMap) {
    if (record.timePcGroups.length === 0)
        return [...new Set(classMap.fallback)];
    const lowered = new Map(Object.entries(classMap.groups).map(([group, classes]) => [group.trim().toLowerCase(), classes]));
    const classes = new Set();
    for (const group of record.timePcGroups)
        for (const id of lowered.get(group.toLowerCase()) ?? [])
            classes.add(id);
    return [...classes];
}
/** Every distinct old PC group named in the rows, for the class-mapping step. */
export function migrationPcGroupsOf(records) {
    const seen = new Map();
    for (const record of records)
        for (const group of record.timePcGroups)
            if (!seen.has(group.toLowerCase()))
                seen.set(group.toLowerCase(), group);
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
}
/** Usernames a café uses for itself rather than a player. Skipped when they hold nothing. */
const HOUSE_ACCOUNT = /^(admin|administrator|guest\d*|test\d*|staff|cashier|manager|owner|demo|default|operator|employee|root|system)$/i;
function statusOf(issues, skip) {
    if (issues.some((issue) => SEVERITY[issue.code] === 'error'))
        return 'error';
    if (skip)
        return 'skipped';
    if (issues.some((issue) => SEVERITY[issue.code] === 'warning'))
        return 'warning';
    return 'ok';
}
/** The whole-file and organisation checks, and each row's status. Rows keep their order. */
export function validateMigrationRows(records, context) {
    const count = (values) => {
        const counts = new Map();
        for (const value of values)
            if (value !== null)
                counts.set(value, (counts.get(value) ?? 0) + 1);
        return counts;
    };
    const sourceIds = count(records.map((record) => record.sourceId));
    const emails = count(records.map((record) => record.email));
    const phones = count(records.map((record) => record.phone));
    // Usernames that differ only in case (or repeat) across different records: HQ decides which is real.
    const usernameKeys = new Map();
    for (const record of records) {
        if (record.username === null)
            continue;
        const key = record.username.toLowerCase();
        const set = usernameKeys.get(key) ?? new Set();
        set.add(`${record.sourceKey}#${record.rowNumber}`);
        usernameKeys.set(key, set);
    }
    return records.map((record) => {
        const issues = [...record.issues];
        if (record.sourceId !== null && (sourceIds.get(record.sourceId) ?? 0) > 1)
            issues.push({ code: 'duplicate-source-id', field: 'sourceId' });
        if (record.username !== null && (usernameKeys.get(record.username.toLowerCase())?.size ?? 0) > 1)
            issues.push({ code: 'username-clash', field: 'username' });
        if (record.email !== null && (emails.get(record.email) ?? 0) > 1)
            issues.push({ code: 'duplicate-email', field: 'email' });
        if (record.phone !== null && (phones.get(record.phone) ?? 0) > 1)
            issues.push({ code: 'duplicate-phone', field: 'phone' });
        if (record.email !== null && context.isBlockedEmail(record.email))
            issues.push({ code: 'email-blocked', field: 'email' });
        if (record.email !== null && context.existingAccountEmails.has(record.email))
            issues.push({ code: 'existing-account', field: 'email' });
        const claimEmail = record.email !== null && !context.isBlockedEmail(record.email);
        if (!claimEmail)
            issues.push({ code: 'counter-only' });
        if (record.cashMinor < 0)
            issues.push({ code: 'negative-cash', field: 'cashBalance' });
        if (record.timeSeconds < 0)
            issues.push({ code: 'negative-time', field: 'timeBalance' });
        if (record.bonusMinor > 0)
            issues.push({ code: 'bonus-dropped', field: 'bonusBalance' });
        const cash = Math.max(0, record.cashMinor);
        const seconds = Math.max(0, record.timeSeconds);
        const stationClassIds = seconds > 0 ? stationClassesForTime(record, context.classMap) : [];
        if (seconds > 0 && stationClassIds.length === 0)
            issues.push({ code: 'time-group-unmapped', field: 'timePcGroups' });
        const needsTick = cash > context.maxWalletBalanceMinor;
        if (needsTick)
            issues.push({ code: 'over-wallet-cap', field: 'cashBalance' });
        const large = cash > context.reviewAmountMinor || seconds > context.reviewSeconds;
        if (large)
            issues.push({ code: 'large-balance' });
        let minor = false;
        if (record.dateOfBirth !== null && record.dateOfBirth <= context.today) {
            minor = ageOn(record.dateOfBirth, context.today) < context.minorAge;
            if (minor)
                issues.push({ code: 'minor', field: 'dateOfBirth' });
        }
        const holdsValue = cash > 0 || seconds > 0;
        let skip = false;
        if (context.alreadyImported.has(record.sourceKey)) {
            issues.push({ code: 'already-imported' });
            skip = true;
        }
        if (record.deleted) {
            issues.push({ code: 'deleted-in-source', field: 'deleted' });
            if (!holdsValue)
                skip = true;
        }
        if (record.username !== null && HOUSE_ACCOUNT.test(record.username)) {
            issues.push({ code: 'house-account', field: 'username' });
            if (!holdsValue)
                skip = true;
        }
        return { record, status: statusOf(issues, skip), issues, stationClassIds, minor, needsTick, large };
    });
}
/** The dry run (owner's decision 9, 11): pure arithmetic over the validated rows; nothing is posted. */
export function summariseMigration(rows, options) {
    const byStatus = { ok: 0, warning: 0, error: 0, skipped: 0 };
    const issueCounts = {};
    let players = 0;
    let cashMinor = 0;
    let timeSeconds = 0;
    let timePlayers = 0;
    let bonusDroppedMinor = 0;
    let owedNotCarriedMinor = 0;
    let minors = 0;
    let claimableByEmail = 0;
    let counterOnly = 0;
    let existingAccounts = 0;
    const largeBalances = [];
    for (const row of rows) {
        byStatus[row.status] += 1;
        for (const issue of row.issues)
            issueCounts[issue.code] = (issueCounts[issue.code] ?? 0) + 1;
        if (row.status !== 'ok' && row.status !== 'warning')
            continue;
        players += 1;
        const record = row.record;
        if (record.cashMinor > 0)
            cashMinor += record.cashMinor;
        else
            owedNotCarriedMinor += -record.cashMinor;
        if (record.timeSeconds > 0) {
            timeSeconds += record.timeSeconds;
            timePlayers += 1;
        }
        if (record.bonusMinor > 0)
            bonusDroppedMinor += record.bonusMinor;
        if (row.minor)
            minors += 1;
        if (row.issues.some((issue) => issue.code === 'counter-only'))
            counterOnly += 1;
        else
            claimableByEmail += 1;
        if (row.issues.some((issue) => issue.code === 'existing-account'))
            existingAccounts += 1;
        if (row.large || row.needsTick) {
            largeBalances.push({
                rowNumber: record.rowNumber,
                sourceKey: record.sourceKey,
                username: record.username,
                cashMinor: Math.max(0, record.cashMinor),
                timeSeconds: Math.max(0, record.timeSeconds),
                needsTick: row.needsTick,
            });
        }
    }
    largeBalances.sort((a, b) => b.cashMinor - a.cashMinor || b.timeSeconds - a.timeSeconds || a.rowNumber - b.rowNumber);
    return {
        rows: rows.length,
        byStatus,
        players,
        cashMinor,
        cashTaxPaidMinor: options.gstPaidAtTopUp ? cashMinor : 0,
        timeSeconds,
        timePlayers,
        bonusDroppedMinor,
        owedNotCarriedMinor,
        minors,
        claimableByEmail,
        counterOnly,
        existingAccounts,
        largeBalances,
        needsTick: largeBalances.filter((balance) => balance.needsTick).length,
        issueCounts,
    };
}
/** The ticks still missing before the import may run: over-cap rows to import whose key HQ has not ticked. */
export function missingMigrationTicks(rows, ticked) {
    return rows.filter((row) => row.needsTick && (row.status === 'ok' || row.status === 'warning') && !ticked.has(row.record.sourceKey)).map((row) => row.record.sourceKey);
}
//# sourceMappingURL=validate.js.map